/**
 * Which privacy model a visitor gets, decided from the country Cloudflare saw.
 *
 * Two models today. "strict" asks before anything optional is stored or run:
 * the cookie banner, Preferences and Analytics off until the visitor says yes,
 * session replay only after that yes. "us" asks nothing up front: basic
 * analytics runs by default, session replay never runs, and the visitor turns
 * analytics off from "Privacy choices" (or their browser's Global Privacy
 * Control signal does it for them).
 *
 * Only the United States gets the consent-free model. Every other country,
 * and a request whose country is unknown, gets the strict one: being wrong in
 * the strict direction costs a banner, being wrong the other way costs a
 * visitor's consent.
 *
 * The model is a row in a table rather than an if/else so a country can move
 * between models without touching the code that applies them. The UK is the
 * likely first case: it may later get consent-free aggregate analytics, which
 * would be a new regime here (say 'uk-aggregate') with its own policy row and
 * a one-line change to GB below.
 */

export type PrivacyRegime = 'strict' | 'us';

export type PrivacyPolicy = {
  /** Shows the cookie banner and waits for an answer before anything optional runs. */
  askFirst: boolean;
  /** Preferences may be kept across visits before the visitor said anything. */
  preferencesByDefault: boolean;
  /** Basic analytics (pageviews, events) may run before the visitor said anything. */
  analyticsByDefault: boolean;
  /** When session replay may record: only after an explicit yes, or never. */
  sessionReplay: 'after-consent' | 'never';
};

export const PRIVACY_POLICIES: Readonly<Record<PrivacyRegime, PrivacyPolicy>> = {
  strict: {
    askFirst: true,
    preferencesByDefault: false,
    analyticsByDefault: false,
    sessionReplay: 'after-consent',
  },
  us: {
    askFirst: false,
    preferencesByDefault: true,
    analyticsByDefault: true,
    sessionReplay: 'never',
  },
};

/**
 * The countries named explicitly. Anything not listed falls back to strict
 * too; the list exists so the decision for each of these is written down and
 * can be changed one country at a time.
 */
export const PRIVACY_REGIME_BY_COUNTRY: Readonly<Record<string, PrivacyRegime>> = {
  // The European Union.
  AT: 'strict', BE: 'strict', BG: 'strict', HR: 'strict', CY: 'strict', CZ: 'strict',
  DK: 'strict', EE: 'strict', FI: 'strict', FR: 'strict', DE: 'strict', GR: 'strict',
  HU: 'strict', IE: 'strict', IT: 'strict', LV: 'strict', LT: 'strict', LU: 'strict',
  MT: 'strict', NL: 'strict', PL: 'strict', PT: 'strict', RO: 'strict', SK: 'strict',
  SI: 'strict', ES: 'strict', SE: 'strict',
  // The rest of the EEA.
  IS: 'strict', LI: 'strict', NO: 'strict',
  // Our choice for now; see the note above about the UK.
  CH: 'strict',
  GB: 'strict',
  TR: 'strict',
  US: 'us',
};

/** A two-letter country code, or null for anything else (Cloudflare's `XX`, Tor's `T1`, nothing). */
export function normalizeCountry(country: string | null | undefined): string | null {
  const code = (country ?? '').trim().toUpperCase();
  if (!/^[A-Z]{2}$/.test(code) || code === 'XX' || code === 'T1') return null;
  return code;
}

export function privacyRegimeForCountry(country: string | null | undefined): PrivacyRegime {
  const code = normalizeCountry(country);
  if (!code) return 'strict';
  return PRIVACY_REGIME_BY_COUNTRY[code] ?? 'strict';
}

/**
 * Whether analytics may join events to a signed-in user id and workspace.
 *
 * Not for visitors in Türkiye (nor where the country is unknown, which could
 * be Türkiye): analytics there runs only after explicit consent, like the EEA,
 * and stays anonymous even then, so no user or workspace id reaches PostHog.
 * Whether a transfer mechanism under KVKK article 9 is in place for a provider
 * is tracked in the compliance records, not decided at run time here.
 */
export function analyticsMayIdentify(country: string | null | undefined): boolean {
  const code = normalizeCountry(country);
  return code !== null && code !== 'TR';
}

/** Reads a regime off the wire; anything unexpected is strict. */
export function parsePrivacyRegime(value: unknown): PrivacyRegime {
  return value === 'us' ? 'us' : 'strict';
}

/** What `GET /api/geo` answers. */
export type GeoAnswer = {
  country: string | null;
  privacyRegime: PrivacyRegime;
  /** The request carried `Sec-GPC: 1`. */
  gpc: boolean;
};
